// InfoTip — info icon with portaled hover tooltip. Default position is to
// the LEFT of the icon so the cursor hovering the icon never overlaps the
// content. Falls back to below-right when there's no room on the left.
//
// Ported from selectors-sidebar.jsx (lines 86-155).

import * as React from "react";
import { createPortal } from "react-dom";

const InfoIcon = ({ size = 11 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="11" x2="12" y2="17" />
    <circle cx="12" cy="7.5" r="0.6" fill="currentColor" />
  </svg>
);

export function InfoTip({ content }: { content: React.ReactNode }) {
  const [show, setShow] = React.useState(false);
  const [pos, setPos] = React.useState<{ left: number; top: number } | null>(
    null,
  );
  const ref = React.useRef<HTMLSpanElement | null>(null);

  const updatePos = React.useCallback(() => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const tipW = 280;
    const gap = 8;
    let left = r.left - tipW - gap;
    let top = r.top - 4;
    if (left < 8) {
      left = Math.max(8, r.left);
      if (left + tipW + 12 > window.innerWidth)
        left = window.innerWidth - tipW - 12;
      top = r.bottom + 6;
    }
    setPos({ left, top });
  }, []);

  React.useEffect(() => {
    if (!show) return;
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [show, updatePos]);

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        tabIndex={0}
        aria-label="More info"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          borderRadius: "50%",
          color: show ? "var(--accent-ink)" : "var(--ink-4)",
          cursor: "help",
          outline: "none",
          transition: "color 120ms ease",
        }}
      >
        <InfoIcon size={11} />
      </span>
      {show && pos &&
        createPortal(
          <div
            data-popover-portal="1"
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              zIndex: 2000,
              width: 280,
              padding: "10px 12px",
              background: "var(--ink)",
              color: "var(--bg)",
              borderRadius: 6,
              fontSize: 11.5,
              lineHeight: 1.55,
              fontWeight: 400,
              letterSpacing: 0,
              textTransform: "none",
              boxShadow: "0 8px 24px -6px rgba(20,30,22,0.32)",
              pointerEvents: "none",
              fontFamily: "var(--font-sans)",
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
